import { 
    AppEnum, 
    AppEnumHelper, 
    DateFormatHelper, 
    MandateEnums, 
    OptionHelper, 
    PropertyEnums,
    SoNumber,
    RecordMap
} from "@/Shared";

import { SectorModel, CityModel, MandateModel, ContactModel } from '@/Domains';

import { PropertyModel } from '../Models';
import { PropertyFormValues } from "../PropertyFormValidation";

export interface PropertyFVMParams {
    viewEnums: PropertyEnums,
    mandateEnums: MandateEnums,
    mandateModelMap: RecordMap<MandateModel>,
    contactModelMap: RecordMap<ContactModel>,
    cityModelMap: RecordMap<CityModel>,
    sectorModelMap: RecordMap<SectorModel>,
    property?: PropertyModel,
    contact? : ContactModel
}


export class PropertyFormViewModel {

    private _sectorOptions = [] as AppEnum[];
    private _cityOptions = [] as AppEnum[];
    private _property?: PropertyModel;
    private _sectorMap!: RecordMap<SectorModel>;
    private _mandateMap!: RecordMap<MandateModel>;
    private _contactMap!: RecordMap<ContactModel>;

    private _typeOptions = [] as AppEnum[];
    private _categoryOptions = [] as AppEnum[];

    private _amenityFeatureOptions = [] as AppEnum[];
    private _serviceFeatureOptions = [] as AppEnum[];

    private _priceRangeOptions = [] as AppEnum[];

    public constructor(protected params: PropertyFVMParams) {

        const { viewEnums,
            mandateEnums,
            mandateModelMap,
            contactModelMap,
            cityModelMap,
            sectorModelMap,
            property } = params;

        this._property = property;
        this._sectorMap = sectorModelMap;
        this._mandateMap = mandateModelMap;
        this._contactMap = contactModelMap;

        this._cityOptions = OptionHelper.cityOptions(cityModelMap.toArray());
        this._categoryOptions = OptionHelper.sortByLabel(viewEnums.PropertyCategoryEnum);
        this.handleCategoryChange(property?.propertyCategory);

        this.handleCityChange(this._property?.sector?.cityId);


        this._amenityFeatureOptions = OptionHelper.sortByLabel(
            AppEnumHelper.getByValue(viewEnums.PropertyFeatureCategoryEnum, 'amenity')?.children ?? []
        );

        this._serviceFeatureOptions = OptionHelper.sortByLabel(
            AppEnumHelper.getByValue(viewEnums.PropertyFeatureCategoryEnum,'service')?.children ?? []
        );

        this._priceRangeOptions = OptionHelper.sortByMinMax(viewEnums.PriceRangeEnum);
    }

    get formValues() {
        const {
            totalPrice, 
            price, 
            priceRange,
            priceRangeLabel
        } = this.handlePriceChange({
            
            price : SoNumber.formatAmount(this._property?.price), 
            serviceChargeProvision : SoNumber.formatAmount(this._property?.serviceChargeProvision),
            acMaintenanceCharge : SoNumber.formatAmount(this._property?.acMaintenanceCharge)
        });

        
        return {
            // Caractéristiques générales
            //mandateId: this.getLastMandateId(),
            furnished: !!this._property?.furnished,

            availability: this._property?.availability ?? "",

            propertyType: this._property?.propertyType ?? "",

            propertyCategory: this._property?.propertyCategory ?? "",

            availableAt: DateFormatHelper.toInput(this._property?.availableAt),

            condition: this._property?.condition ?? "",

            yearBuilt: this._property?.yearBuilt ?? "",

            // Address / Secteur
            sectorId: this._property?.sectorId ?? "",

            address: this._property?.address ?? "",


            // Pièces
            roomCount: this._property?.roomCount ?? "",

            surfaceArea: this._property?.surfaceArea ?? "",

            amenityFeatures: this._property?.amenityFeatures ?? [],

            serviceFeatures: this._property?.serviceFeatures ?? [],


            standing: this._property?.standing ?? "",

            operationType: this._property?.operationType ?? "",

            price : SoNumber.formatAmount(price),

            serviceChargeProvision: SoNumber.formatAmount(this._property?.serviceChargeProvision),

            acMaintenanceCharge: SoNumber.formatAmount(this._property?.acMaintenanceCharge),

            securityDeposit: SoNumber.formatAmount(this._property?.securityDeposit),

            totalPrice : (this._property || SoNumber.parceAmout(totalPrice) > 0) ? totalPrice : "",

            priceRange,
            priceRangeLabel,

            // Autres
            description: this._property?.description ?? "",

            cityId: this._property?.sector?.cityId ?? "",

            owners: this._property?.propertyOwnerLinks?.map(link => String(link.ownerId) ) ?? [],

            images: [],

            videos: [],

            videoURLs: [],

            usageType: this._property?.usageType ?? "",
        } as PropertyFormValues;
    }


    handleCategoryChange(newValue?: string | null) {
        const newTypes = this._categoryOptions.find(cat => cat.value === newValue)?.children;

        this._typeOptions = newTypes ? OptionHelper.sortByLabel(newTypes) : [];
        return this._typeOptions?.[0]?.value;
    }


    handleCityChange(newCityId?: number | string | null) {

        this._sectorOptions = newCityId
            ? OptionHelper.sectorOptions(
                    this._sectorMap.filter(sector => sector.cityId === Number(newCityId)).toArray()
                )
            : [];

        return this._sectorOptions?.[0]?.value;

    }

    handleChargeChange(payload : {
        serviceChargeProvision?: string | null, 
        acMaintenanceCharge?: string | null, 
        price?: string | null
    }) {
        const {
            serviceChargeProvision, 
            acMaintenanceCharge, 
            totalPrice, 
            price 
        } = this.computePricesChange(payload);

        return {
            serviceChargeProvision : SoNumber.formatAmount(serviceChargeProvision),
            acMaintenanceCharge : SoNumber.formatAmount(acMaintenanceCharge),
            totalPrice : SoNumber.formatAmount(totalPrice),
            price : SoNumber.formatAmount(price)
        };
    }

    computePricesChange(payload : {
        serviceChargeProvision?: string | null, 
        acMaintenanceCharge?: string | null, 
        price?: string | null
    }) {
        const serviceChargeProvision = SoNumber.parceAmout(payload.serviceChargeProvision);
        const acMaintenanceCharge =  SoNumber.parceAmout(payload.acMaintenanceCharge);
        const price =  SoNumber.parceAmout(payload.price+"");

        const totalPrice = serviceChargeProvision + acMaintenanceCharge + price;
        
        return {
            serviceChargeProvision : serviceChargeProvision > 0 ? serviceChargeProvision : null,
            acMaintenanceCharge : acMaintenanceCharge > 0 ? acMaintenanceCharge : null,
            totalPrice,
            price : price > 0 ? price : null
        };
    }

    handlePriceChange(payload : {
            serviceChargeProvision?: string | null, 
            acMaintenanceCharge?: string | null, 
            price?: string | null
        }) {
        
        const {serviceChargeProvision, acMaintenanceCharge, totalPrice, price } = this.computePricesChange(payload);

        const numericPrice = price ?? 0;
        
        const priceRange = this._priceRangeOptions.find(range =>
            numericPrice >= (range.min ?? 0) && (!range.max || numericPrice <= range.max)
        ) ?? this._priceRangeOptions[0];

        return { 
            serviceChargeProvision : SoNumber.formatAmount(serviceChargeProvision),
            acMaintenanceCharge : SoNumber.formatAmount(acMaintenanceCharge),
            totalPrice : SoNumber.formatAmount(totalPrice),
            price : SoNumber.formatAmount(price),
            priceRange : priceRange.value,
            priceRangeLabel : priceRange.label ?? "" 
        };
    }

    get propertyUsageOptions(): AppEnum[] {
        return OptionHelper.sortByLabel(this.params.viewEnums.PropertyUsageEnum);
    }

    get amenityFeatureOptions() {
        return this._amenityFeatureOptions;
    }

    get serviceFeatureOptions() {
        return this._serviceFeatureOptions;
    }

    get priceRangeOptions(): AppEnum[] {
        return this._priceRangeOptions;
    }

    get id() {
        return this._property?.id;
    }

    get categoryOptions(): AppEnum[] {
        return this._categoryOptions;
    }

    get typeOptions(): AppEnum[] {
        return this._typeOptions;
    }

    get standingOptions(): AppEnum[] {
        return OptionHelper.sortByLabel(this.params.viewEnums.StandingEnum);
    }

    get operationOptions(): AppEnum[] {
        return OptionHelper.sortByLabel(this.params.viewEnums.OperationTypeEnum);
    }

    get cityOptions(): AppEnum[] {
        return this._cityOptions;
    }

    get sectorOptions(): AppEnum[] {
        return this._sectorOptions;
    }

    get conditionOptions(): AppEnum[] {
        return OptionHelper.sortByLabel(this.params.viewEnums.PropertyConditionEnum);
    }

    get availabilityOptions(): AppEnum[] {
        return OptionHelper.sortByLabel(this.params.viewEnums.AvailabilityEnum);
    }

    get ownerOptions(): AppEnum[] {
        return OptionHelper.ownerOptions(this._contactMap.toArray());
    }

    getTitle = () => {
        return this._property 
        ? "Bien N° " + this.id + " - " + this._property?.propertyCategory?.toUpperCase() 
        : "Création de bien";
    }

    isCreationMode = () => !this._property?.id;
}